<script setup lang="ts">
import {computed, reactive, ref, watch} from "vue";
import countries from "@/Data/countries-array.ts";
import {deleteAvatar as apiDeleteAvatar, updateUser, updatePreferences} from "@/Services/users";
import {authUser, updatePassword} from "@/Services/auth";
import {
    Badge,
    Button,
    Card,
    ColorPicker,
    Dialog,
    FileUpload,
    Icon,
    InputText,
    Label,
    Password,
    Select,
    Tag,
    Textarea,
    SelectButton,
} from "@/Components/Library";
import {PageTitle} from "@/Components/Global";
import {saving} from "@/Composables/api";
import {useMainStore} from "@/Stores/store";
import PageContainer from "@/Components/Global/PageContainer.vue";
import {PlanRestrictions} from "@/Enums";
import {sendSupportRequest} from "@/Services/support";
import {useToast} from "primevue/usetoast";

const $store = useMainStore();
const toast = useToast();

const avatarUploadRef = ref<typeof FileUpload>();

const notificationFrequencyOptions = [
    {label: "Off", value: "off"},
    {label: "Hourly", value: "hourly"},
    {label: "Daily", value: "daily"},
    {label: "Weekly", value: "weekly"},
];

const detailsForm = reactive({
    id: $store.user.id,
    first_name: $store.user.first_name,
    last_name: $store.user.last_name,
    nickname: $store.user.nickname,
    email: $store.user.email,
    profile_id: $store.user.profile?.id ?? null,
    country: $store.user.profile?.country ?? null,
    color: $store.user.profile?.color?.slug ?? null,
});

const preferences = reactive({
    general_notification_frequency: $store.user.preferences?.general_notification_frequency ?? "daily",
    personal_notification_frequency: $store.user.preferences?.personal_notification_frequency ?? "daily",
});

const passwordForm = reactive({
    current_password: null,
    password: null,
    password_confirmation: null,
});

const saveDetails = async () => {
    const {data} = await updateUser(detailsForm);
    Object.assign($store.user, data);
    await avatarUploadRef.value?.startUpload();
    $store.toastMessages.push({
        severity: "success",
        summary: "User updated",
        life: 3000,
    });
};

const savePassword = async () => {
    await updatePassword($store.user.id, passwordForm);
    $store.toastMessages.push({
        severity: "success",
        summary: "Password updated",
        life: 3000,
    });
};

const savePreferences = async () => {
    await updatePreferences($store.user, preferences);
    $store.user.preferences = {...$store.user.preferences, ...preferences};
    $store.toastMessages.push({
        severity: "success",
        summary: "Preferences updated",
        life: 3000,
    });
};

const deleteAvatar = async () => {
    await apiDeleteAvatar($store.user.profile.id, $store.user.profile.avatar.id);
    $store.user.profile.avatar = null;
    $store.toastMessages.push({
        severity: "success",
        summary: "Avatar deleted",
        life: 3000,
    });
};

const handleAvatarUpload = () => authUser().then(({data}) => Object.assign($store.user, data));

const showUpgradeDialog = ref(false);
const upgradeForm = reactive({message: ""});

const submitUpgradeRequest = async () => {
    await sendSupportRequest({
        subject: "Upgrade Plan Inquiry",
        message: upgradeForm.message,
        route: "/profile",
    });
    upgradeForm.message = "";
    toast.add({
        severity: "success",
        summary: "Inquiry sent",
        detail: "We will get back to you as soon as possible.",
        life: 3000,
    });
    setTimeout(() => (showUpgradeDialog.value = false), 500);
};

watch(preferences, savePreferences, {deep: true});
</script>
<template>
    <PageContainer class="max-w-7xl">
        <PageTitle class="mb-2" title="Update your profile" />
        <div class="mb-8 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div class="col-span-2">
                <div class="mb-8">
                    <form @submit.prevent="saveDetails">
                        <Card class="mb-4">
                            <template #title>Your details</template>
                            <template #content>
                                <div class="mb-6 mt-2 flex flex-col gap-3">
                                    <div class="grid gap-4 lg:grid-cols-2">
                                        <div class="flex flex-col">
                                            <Label for="first_name" class="mb-1" required>First name</Label>
                                            <InputText
                                                id="first_name"
                                                name="first_name"
                                                type="text"
                                                placeholder="First name"
                                                v-model="detailsForm.first_name"
                                                fluid
                                                required
                                            />
                                        </div>
                                        <div class="flex flex-col">
                                            <Label for="last_name" class="mb-1" required>Last name</Label>
                                            <InputText
                                                id="last_name"
                                                name="last_name"
                                                type="text"
                                                placeholder="Last name"
                                                v-model="detailsForm.last_name"
                                                fluid
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div class="flex flex-col">
                                        <Label for="nickname" class="mb-1" required>Nickname</Label>
                                        <InputText
                                            id="nickname"
                                            name="nickname"
                                            type="text"
                                            placeholder="Nickname"
                                            v-model="detailsForm.nickname"
                                            fluid
                                            required
                                        />
                                        <!--                                <small v-if="errors.nickname" class="mt-2 text-red-500">{{ errors.first_name }}</small>-->
                                        <span class="mt-1 text-xs">
                                            This will be your name displayed in public groups
                                        </span>
                                    </div>
                                </div>
                                <div class="mb-6 flex flex-col">
                                    <Label for="email" class="mb-1" required>Email</Label>
                                    <InputText
                                        id="email"
                                        name="email"
                                        type="email"
                                        placeholder="Email"
                                        v-model="detailsForm.email"
                                        fluid
                                        required
                                    />
                                </div>
                                <div class="mb-8 flex flex-col">
                                    <Label for="country" class="mb-1">Country</Label>
                                    <Select
                                        id="country"
                                        name="country"
                                        v-model="detailsForm.country"
                                        :options="countries"
                                        placeholder="Select a country"
                                        fluid
                                    />
                                </div>
                            </template>
                        </Card>
                        <Card class="mb-4">
                            <template #content>
                                <h3 class="mb-1 font-bold">Color</h3>
                                <div class="mb-4 flex flex-col py-2">
                                    <ColorPicker v-model="detailsForm.color" />
                                </div>
                                <div class="flex flex-col">
                                    <div
                                        :key="$store.user.profile?.avatar?.url ?? 0"
                                        v-if="$store.user.profile?.avatar"
                                        class="relative mx-auto mb-3 aspect-square max-h-48 max-w-48"
                                    >
                                        <Button
                                            class="!absolute right-0 top-0 z-20 !p-1 text-red-600 hover:bg-red-600 hover:text-white"
                                            :icon="{
                                                icon: 'mdi:close',
                                                width: '20',
                                            }"
                                            @click.stop="deleteAvatar"
                                        />
                                        <div class="h-full w-full overflow-hidden rounded-full">
                                            <img
                                                :src="$store.user.profile.avatar.url"
                                                :alt="detailsForm.first_name"
                                                class="h-full w-full object-cover"
                                            />
                                        </div>
                                    </div>
                                    <h3 class="mb-1 font-bold">Avatar</h3>
                                    <FileUpload
                                        ref="avatarUploadRef"
                                        id="avatar_image"
                                        name="avatar_image"
                                        collection="avatars"
                                        :parent-id="detailsForm.profile_id"
                                        parent-type="user-profile"
                                        accept="image/*"
                                        @upload="handleAvatarUpload"
                                    />
                                </div>
                            </template>
                        </Card>
                        <div class="">
                            <Button
                                type="submit"
                                :loading="!!saving.includes('user')"
                                icon="mdi:check"
                                class="w-full bg-askr-green text-white"
                            >
                                Update
                            </Button>
                        </div>
                    </form>
                </div>
                <form @submit.prevent="savePassword">
                    <Card class="mb-4">
                        <template #title>Password</template>
                        <template #content>
                            <div class="mb-6 mt-2 flex flex-col">
                                <Label for="current_password" class="mb-1" required>Current password</Label>
                                <Password
                                    inputId="current_password"
                                    name="current_password"
                                    placeholder="Current password"
                                    fluid
                                    required
                                    toggleMask
                                    :feedback="false"
                                    v-model="passwordForm.current_password"
                                />
                            </div>
                            <div class="mb-6 flex flex-col">
                                <Label for="password" class="mb-1" required>New password</Label>
                                <Password
                                    inputId="password"
                                    name="password"
                                    placeholder="New password"
                                    fluid
                                    required
                                    toggleMask
                                    :feedback="false"
                                    v-model="passwordForm.password"
                                />
                            </div>
                            <div class="flex flex-col">
                                <Label for="password_confirmation" class="mb-1" required>Confirm new password</Label>
                                <Password
                                    inputId="password_confirmation"
                                    name="password_confirmation"
                                    placeholder="Confirm new password"
                                    fluid
                                    required
                                    toggleMask
                                    :feedback="false"
                                    v-model="passwordForm.password_confirmation"
                                />
                            </div>
                        </template>
                    </Card>
                    <div class="">
                        <Button
                            type="submit"
                            :loading="!!saving.includes('password')"
                            icon="mdi:lock"
                            class="w-full bg-askr-green text-white"
                        >
                            Update password
                        </Button>
                    </div>
                </form>
            </div>
            <div>
                <div class="mb-6 flex flex-col">
                    <h3 class="mb-1 font-semibold">Email Summary Frequency</h3>
                    <h4 class="text-sm font-medium">Personal</h4>
                    <SelectButton
                        id="notification_frequency"
                        v-model="preferences.personal_notification_frequency"
                        :options="notificationFrequencyOptions"
                        option-label="label"
                        option-value="value"
                        :allow-empty="false"
                        fluid
                    />
                    <!--                    <h4 class="text-sm font-medium">General</h4>-->
                    <!--                    <SelectButton-->
                    <!--                        id="notification_frequency"-->
                    <!--                        v-model="preferences.general_notification_frequency"-->
                    <!--                        :options="notificationFrequencyOptions"-->
                    <!--                        option-label="label"-->
                    <!--                        option-value="value"-->
                    <!--                        :allow-empty="false"-->
                    <!--                        fluid-->
                    <!--                    />-->
                </div>
                <Card v-if="$store.user.plan" class="mb-4">
                    <template #title>
                        <div class="flex items-center justify-between">
                            <span>Your Plan</span>
                            <Tag
                                :value="$store.user.plan.name"
                                :severity="$store.user.plan.slug === 'free' ? 'secondary' : 'success'"
                            />
                        </div>
                    </template>
                    <template #content>
                        <p v-if="$store.user.plan.description" class="mb-4">
                            {{ $store.user.plan.description }}
                        </p>
                        <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
                            <div class="space-y-2 text-sm">
                                <template v-for="feature in PlanRestrictions.ofType('boolean')" :key="feature.slug">
                                    <div class="flex items-start gap-2">
                                        <Icon
                                            :icon="
                                                $store.user.plan[feature.slug] ? 'mdi:check-circle' : 'mdi:close-circle'
                                            "
                                            :class="$store.user.plan[feature.slug] ? 'text-green-500' : 'text-red-500'"
                                            width="18"
                                            class="flex-none"
                                        />
                                        <span>{{ feature.name }}</span>
                                    </div>
                                </template>
                            </div>
                            <div class="space-y-1 text-sm">
                                <template v-for="limit in PlanRestrictions.ofType('number')" :key="limit.slug">
                                    <div class="flex items-start gap-1">
                                        <span class="mr-auto">{{ limit.name }}</span>
                                        <span class="rounded-full bg-sky-700 px-2 py-0.5 font-bold text-white">
                                            <Icon
                                                v-if="$store.user.plan[limit.slug] === -1"
                                                icon="ion:infinite"
                                                width="20"
                                                class="flex-none"
                                            />
                                            <span v-else>{{ $store.user.plan[limit.slug] }}</span>
                                        </span>
                                    </div>
                                </template>
                            </div>
                        </div>
                        <div v-if="$store.user.plan.slug !== 'enterprise'" class="mt-6">
                            <Button
                                class="w-full bg-askr-green text-white hover:brightness-110"
                                outlined
                                @click="showUpgradeDialog = true"
                            >
                                Upgrade Now
                            </Button>
                        </div>
                    </template>
                </Card>
            </div>
        </div>
        <Dialog modal header="Upgrade Plan" v-model:visible="showUpgradeDialog" class="w-full max-w-xl">
            <form @submit.prevent="submitUpgradeRequest">
                <div class="mb-6 flex flex-col">
                    <Label for="message" class="mb-1" required>Message</Label>
                    <Textarea
                        v-model="upgradeForm.message"
                        id="message"
                        name="message"
                        rows="5"
                        autoResize
                        fluid
                        required
                    />
                </div>
                <Button
                    type="submit"
                    class="w-full bg-askr-green text-white hover:brightness-110"
                    :loading="saving.includes('support')"
                >
                    Send
                </Button>
            </form>
        </Dialog>
    </PageContainer>
</template>
